import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  type AppLang,
  getInitialLang,
  htmlLangForLang,
  LANG_STORAGE_KEY,
} from "@/locale/appLang";
import { pickStrings } from "@/locale/strings";

type LocaleContextValue = {
  lang: AppLang;
  setLang: (lang: AppLang) => void;
  t: (key: string) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<AppLang>(() => getInitialLang());

  const setLang = useCallback((next: AppLang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const dict = pickStrings(lang);

  const t = useCallback(
    (key: string) => {
      const v = dict[key];
      if (v !== undefined) return v;
      return key;
    },
    [dict],
  );

  useEffect(() => {
    document.documentElement.lang = htmlLangForLang(lang);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}
